import { swSettings } from "app/common/services/swSettings";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import queryString from "query-string";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { apiHelper } from "app/common/services/apiHelper";
import _ from "lodash";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import {
    getCommonColumns,
    getSingleColumns,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/ColumnsConfig";
import { AdvertisersTableTopSingle } from "app/pages/website-analysis/analyze-publishers/advertisers/single/AdvertisersTableTopSingle";
import type {
    IAdvertisersTableProps,
    IFilterItem,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import CountryService from "app/services/CountryService";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";

const fetchService = DefaultFetchService.getInstance();

const AdvertisersTableSingle: FC<IAdvertisersTableProps> = (props) => {
    const { i18n, getLink } = props;
    const { params, state } = useCurrentStateAndParams();
    const handleOpenQualificationSideBar =
        !!state.qualificationSideBarComponent && props.handleOpenQualificationSideBar;
    const { webSource, orderBy, duration, network, country, isWWW, key, industry, search } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const durationObject = useMemo(() => DurationService.getDurationData(duration), [duration]);
    const fromDate = durationObject.raw.from;
    const toDate = durationObject.raw.to;
    const [totalSectionData, setTotalSectionData] = useState<{
        totalShare: number;
        totalUnGroupedCount: number;
        totalVisits: number;
    }>();
    const [filtersData, setFiltersData] = useState<{
        Networks: IFilterItem[];
        Categories: IFilterItem[];
    }>();
    const [topCategories, setTopCategories] = useState<IFilterItem[]>();
    const [networks, setNetworks] = useState<IFilterItem[]>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const changePageCallback = (page) => setPageNumber(page);
    const chosenItems = chosenSites.sitelistForLegend();

    let field = "Share";
    let sortDirection = "desc";
    if (orderBy) {
        [field, sortDirection] = orderBy.split(" ");
    }
    const durationTooltip = _.mapValues(
        DurationService.getDurationData(duration ? duration : "").forTooltip as dateForTooltip,
        function (v) {
            return decodeURIComponent(v);
        },
    );
    const durationTooltipParams = {
        currentMonth: durationTooltip.to,
        lastMonth: durationTooltip.from,
    };
    const columns = [
        ...getCommonColumns(getLink, handleOpenQualificationSideBar),
        ...getSingleColumns(durationTooltipParams),
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable !== false,
        };
    });
    const title = i18n("analysis.monetization.advertisers.title");
    const tooltip = i18n("analysis.monetization.advertisers.title.tooltip");
    const subtitleFilters = useMemo(
        () => [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: duration !== "28d" && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
            },
            {
                filter: "country",
                countryCode: country,
                value: CountryService.getCountryById(country)?.text,
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ],
        [fromDate, toDate, country, webSource, duration],
    );

    useEffect(() => {
        // setTimeout in order to prevent the "$digest already in progress" error
        setTimeout(() => {
            if (orderBy && !columns.find((col) => col.field === field)) {
                onSort({ field: "Share", sortDirection: "desc" });
            }
        }, 0);
    }, []);

    // check if there is a selected filter and if it's supported in the new page state
    useEffect(() => {
        if (filtersData) {
            const isNetworkSupported = network && !filtersData.Networks.find((n) => n.Name === network);
            const isIndustrySupported =
                industry && !filtersData.Categories.find((c) => c.Name.includes(industry.replace("~", "/")));

            if (isNetworkSupported || isIndustrySupported) {
                proNavigator.applyUpdateParams({
                    ...(isIndustrySupported && { industry: null }),
                    ...(isNetworkSupported && { network: null }),
                });
            }
        }
    }, [filtersData]);

    useEffect(() => {
        async function fetchData() {
            setFiltersData(null);
            const response = await fetchService.get<{
                Networks: IFilterItem[];
                Categories: IFilterItem[];
            }>("/api/websiteanalysis/GetTrafficDisplayPaidOutgoing", {
                country,
                from,
                isWWW: isWWW !== "*",
                isWindow,
                key,
                orderBy: "Share desc",
                to,
                webSource,
            });
            setFiltersData(response);
        }

        fetchData();
    }, []);

    const onSort = ({ field, sortDirection }) => {
        proNavigator.applyUpdateParams({
            orderBy: `${field} ${sortDirection}`,
        });
    };

    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;

    const getExcelUrl = () => {
        const params = dataParamsAdapter(getInitialFilters());
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetTrafficDisplayPaidOutgoingWebsitesTsv?${queryStringParams}`;
    };

    const getInitialFilters = () => {
        const copiedParams = { ...params };
        // orderBy
        copiedParams.sort = field;
        copiedParams.asc = sortDirection === "asc" || false;

        if (!copiedParams.page) {
            copiedParams.page = pageNumber;
        }
        return apiHelper.transformParamsForAPI(copiedParams);
    };

    const dataParamsAdapter = (params) => {
        const transformedParams = { ...params };

        if (transformedParams.sort) {
            transformedParams.orderBy = `${params.sort} ${params.asc ? "asc" : "desc"}`;
            delete transformedParams.sort;
            delete transformedParams.asc;
        }

        const filters = [];
        if (transformedParams.network) {
            filters.push(`network;==;"${decodeURIComponent(transformedParams.network)}"`);
            delete transformedParams.network;
        }
        if (transformedParams.industry) {
            filters.push(`category;category;"${decodeURIComponent(transformedParams.industry)}"`);
            delete transformedParams.industry;
        }
        if (transformedParams.search) {
            filters.push(`domain;contains;"${transformedParams.search}"`);
            delete transformedParams.search;
        }
        if (filters.length > 0) {
            transformedParams.filter = filters.join(",");
        }

        return transformedParams;
    };
    const transformData = (data) => {
        const stateName = "digitalsuite_website_websiteperformance";

        return {
            ...data,
            Records: data.Records.map((record: any) => {
                return {
                    ...record,
                    url: proNavigator.href(stateName, {
                        ...params,
                        key: record.Domain,
                    }),
                    Category: record.Category.replace(/_/g, " ").replace("~", " > "),
                    CategoryApi: record.Category,
                    Children: Array.isArray(record.Children)
                        ? record.Children.map((child) => {
                              return {
                                  ...child,
                                  url: proNavigator.href(stateName, {
                                      ...params,
                                      key: child.Domain,
                                  }),
                                  Category: record.Category.replace(/_/g, " ").replace("~", " > "),
                                  CategoryApi: record.Category,
                              };
                          })
                        : null,
                };
            }),
        };
    };

    const onGetData = (data) => {
        setTotalSectionData({
            totalShare: data.TotalShare > 0.99 ? 1 : data.TotalShare,
            totalUnGroupedCount: data.TotalUnGroupedCount,
            totalVisits: data.TotalVisits,
        });

        setTopCategories(data.Categories[key]);
        setNetworks([...data.Networks[key]]);
    };

    return (
        <div data-automation="advertisers-single">
            <SWReactTableWrapper
                tableOptions={{
                    metric: "TrafficSourcesAdvertisers",
                    defaultSorted: "Share",
                    noDataTitle: i18n("analysis.publishers.advertisers.empty.state.title"),
                }}
                serverApi={`/api/websiteanalysis/GetTrafficDisplayPaidOutgoingWebsitesTable`}
                tableColumns={columns}
                initialFilters={getInitialFilters()}
                recordsField="Records"
                totalRecordsField="TotalCount"
                onSort={onSort}
                pageIndent={1}
                transformData={transformData}
                getDataCallback={onGetData}
                dataParamsAdapter={dataParamsAdapter}
                changePageCallback={changePageCallback}
            >
                {(topComponentProps) => (
                    <AdvertisersTableTopSingle
                        downloadExcelPermitted={downloadExcelPermitted}
                        excelLink={getExcelUrl()}
                        chosenItems={chosenItems}
                        searchValue={search}
                        i18n={i18n}
                        totalSectionData={totalSectionData}
                        filtersData={filtersData}
                        network={network}
                        category={industry}
                        topCategories={topCategories}
                        networks={networks}
                        getLink={getLink}
                        title={title}
                        tooltip={tooltip}
                        subtitleFilters={subtitleFilters}
                        {...topComponentProps}
                    />
                )}
            </SWReactTableWrapper>
        </div>
    );
};

export default QualificationSideBarHOC(AdvertisersTableSingle);
